import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, Menu, ShieldCheck, X } from 'lucide-react';
import { Contact } from './pages/Contact';
import { About } from './pages/About';
import { CaseStudies } from './pages/CaseStudies';
import { Insights } from './pages/Insights';
import { Industries } from './pages/Industries';
import { Services } from './pages/Services';
import { SupabaseConnected } from './pages/SupabaseConnected';
import { differentiators, focusAreas, heroProof, outcomes, trustedOrganisations } from './data/site';

type Page = 'home' | 'services' | 'case-studies' | 'industries' | 'about' | 'insights' | 'contact' | 'supabase';

const pageTitles: Record<Page, string> = {
  home: 'SVK Limited | Enterprise API & Platform Engineering Consultant',
  services: 'Services | SVK Limited',
  'case-studies': 'Case Studies | SVK Limited',
  industries: 'Industries | SVK Limited',
  about: 'About | SVK Limited',
  insights: 'Insights | SVK Limited',
  contact: 'Contact | SVK Limited',
  supabase: 'Supabase Connection | SVK Limited',
};

const pageDescriptions: Record<Page, string> = {
  home:
    'Enterprise API and platform engineering consultant helping businesses modernise enterprise platforms with secure, scalable, cloud-native engineering.',
  services:
    'API platform engineering, cloud-native architecture, enterprise web applications, DevSecOps, identity, and platform modernisation services.',
  'case-studies':
    'Commercial consulting offers for API modernisation, cloud architecture assessment, and enterprise platform delivery.',
  industries:
    'Enterprise technology consulting for government, finance, retail, regulatory, startup, and integration-led organisations.',
  about:
    'SVK Limited is led by an enterprise platform engineer and technical architect with 17+ years delivering large-scale software platforms.',
  insights:
    'Technical-commercial insight topics covering API-first architecture, Azure API Management, Kubernetes, DevSecOps, IAM, and AI-ready platforms.',
  contact:
    'Contact SVK Limited to discuss API modernisation, cloud architecture assessment, platform engineering, or enterprise technology consulting.',
  supabase: 'Supabase connection test for SVK Limited.',
};

const navItems: Array<{ page: Page; label: string }> = [
  { page: 'home', label: 'Home' },
  { page: 'services', label: 'Services' },
  { page: 'case-studies', label: 'Case Studies' },
  { page: 'industries', label: 'Industries' },
  { page: 'about', label: 'About' },
  { page: 'insights', label: 'Insights' },
  { page: 'contact', label: 'Contact' },
];

const pages: Page[] = ['home', 'services', 'case-studies', 'industries', 'about', 'insights', 'contact', 'supabase'];
const isPage = (value: string): value is Page => pages.includes(value as Page);

export default function App() {
  const [page, setPage] = useState<Page>(() => {
    const hash = window.location.hash.replace('#', '');
    return isPage(hash) ? hash : 'home';
  });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      setPage(isPage(hash) ? hash : 'home');
      setMenuOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    document.title = pageTitles[page];
    document.querySelector('meta[name="description"]')?.setAttribute('content', pageDescriptions[page]);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', pageTitles[page]);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', pageDescriptions[page]);
  }, [page]);

  const content = useMemo(() => {
    if (page === 'services') return <Services onNavigate={setPage} />;
    if (page === 'case-studies') return <CaseStudies onNavigate={setPage} />;
    if (page === 'industries') return <Industries onNavigate={setPage} />;
    if (page === 'about') return <About onNavigate={setPage} />;
    if (page === 'insights') return <Insights onNavigate={setPage} />;
    if (page === 'contact') return <Contact />;
    if (page === 'supabase') return <SupabaseConnected />;
    return <Home onNavigate={setPage} />;
  }, [page]);

  const navigate = (nextPage: Page) => {
    window.location.hash = nextPage === 'home' ? '' : nextPage;
    setPage(nextPage);
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-cloud text-ink">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <button
            className="flex items-center gap-3 text-left"
            onClick={() => navigate('home')}
            aria-label="SVK Limited home"
          >
            <img
              src="/images/svk-logo.svg"
              alt=""
              aria-hidden="true"
              className="h-10 w-20 rounded bg-black object-contain"
            />
            <span>
              <span className="block text-base font-bold">SVK Limited</span>
              <span className="block text-xs text-slate-500">Enterprise technology consultant</span>
            </span>
          </button>

          <button
            className="grid size-10 place-items-center rounded border border-slate-200 text-slate-700 lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <div
            className={`absolute left-0 right-0 top-full border-b border-slate-200 bg-white px-5 py-4 shadow-soft lg:static lg:flex lg:items-center lg:gap-1 lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none ${
              menuOpen ? 'block' : 'hidden lg:flex'
            }`}
          >
            {navItems.map((item) => (
              <button
                key={item.page}
                className={`block w-full rounded px-3 py-2 text-left text-sm font-semibold transition lg:w-auto ${
                  page === item.page ? 'bg-ink text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
                onClick={() => navigate(item.page)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <main>{content}</main>

      <footer className="bg-ink text-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-10 md:grid-cols-[1fr_auto] lg:px-8">
          <div>
            <p className="text-lg font-bold">SVK Limited</p>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
              Enterprise API and platform engineering consulting for secure, scalable, cloud-native digital platforms.
            </p>
          </div>
          <div className="text-sm text-slate-300">
            <p>support@svklimited.co.uk</p>
            <p>United Kingdom</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Home({ onNavigate }: { onNavigate: (page: Page) => void }) {
  return (
    <>
      <section className="relative isolate overflow-hidden bg-ink text-white">
        <img
          src="/images/svk-office-hero.png"
          alt="Enterprise technology consulting team reviewing platform architecture"
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-24"
        />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(15,23,42,0.96),rgba(15,23,42,0.84),rgba(15,23,42,0.56))]" />
        <div className="mx-auto grid min-h-[calc(100vh-76px)] max-w-7xl content-center gap-10 px-5 py-20 lg:grid-cols-[1fr_410px] lg:px-8">
          <div className="max-w-4xl">
            <p className="mb-5 inline-flex rounded bg-cyan/15 px-3 py-1 text-sm font-semibold text-cyan backdrop-blur">
              SVK Limited Enterprise Technology Consultant
            </p>
            <h1 className="max-w-5xl text-5xl font-bold leading-[1.02] md:text-7xl">
              Enterprise API & Platform Engineering Consultant
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-100 md:text-xl">
              Designing and delivering scalable cloud-native platforms, API ecosystems, and enterprise web applications
              for government, finance, retail, and high-growth businesses.
            </p>
            <p className="mt-5 max-w-3xl text-base leading-7 text-slate-300">
              17+ years delivering enterprise-grade software platforms across public and private sectors, specialising in
              API architecture, Azure platform ecosystems, microservices, DevSecOps, identity, and scalable web
              applications.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <button className="btn-primary" onClick={() => onNavigate('contact')}>
                Book a Consultation <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button className="btn-secondary" onClick={() => onNavigate('contact')}>
                Discuss Your Platform
              </button>
              <button className="btn-secondary" onClick={() => onNavigate('case-studies')}>
                API Modernisation Review
              </button>
              <button className="btn-secondary" onClick={() => onNavigate('case-studies')}>
                Cloud Architecture Assessment
              </button>
            </div>
          </div>

          <aside className="self-end border border-white/18 bg-white/10 p-5 backdrop-blur">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-200">Focused on</p>
            <div className="mt-5 grid gap-3">
              {focusAreas.slice(0, 7).map((item) => (
                <p key={item} className="flex items-center gap-3 text-sm text-white">
                  <CheckCircle2 className="shrink-0 text-cyan" size={18} />
                  {item}
                </p>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <div className="grid gap-5 md:grid-cols-3">
          {heroProof.map((item) => (
            <div key={item} className="border-l-4 border-cyan bg-white p-6 shadow-soft">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-slate-500">Proof point</p>
              <p className="mt-3 text-lg font-bold">{item}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="section-eyebrow">Commercial Positioning</p>
              <h2 className="section-title">Helping enterprises build scalable, secure, cloud-native digital platforms.</h2>
              <p className="mt-5 text-base leading-7 text-slate-600">
                This is technical consulting for organisations that need more than coding: platform transformation,
                architecture leadership, delivery acceleration, and modern engineering practices that can survive
                enterprise scale.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {outcomes.map((item) => (
                <div key={item} className="rounded border border-slate-200 p-4">
                  <ShieldCheck className="text-cyan" size={20} aria-hidden="true" />
                  <p className="mt-3 text-sm font-bold">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="max-w-3xl">
          <p className="section-eyebrow">Why SVK Limited</p>
          <h2 className="section-title">Enterprise architecture with hands-on engineering credibility.</h2>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {differentiators.map((item) => (
            <article key={item.title} className="rounded border border-slate-200 bg-white p-6 shadow-soft">
              <item.icon className="text-cyan" size={28} aria-hidden="true" />
              <h3 className="mt-5 text-xl font-bold">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-ink text-white">
        <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-cyan">Trusted sector experience</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {trustedOrganisations.map((name) => (
              <div key={name} className="border border-white/12 bg-white/6 px-4 py-3 text-sm font-bold text-slate-100">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
